<template>
    <div :class="[layoutClasses]" :data-col="isNavMenuHidden ? '1-column' : null" class="vertical-layout h-100">
        <!-- APPS MODAL -->
        <!--  // Only used for email at the moment.-->
        <deprecated-app-modal />
        <!-- This one is used since conversations layer -->
        <app-modal />

        <!-- TOURS -->
        <tour v-if="getInstance().isAuthenticated" :options="{ startTimeout: 2000 }" module="inbox" />
        <tour v-if="getInstance().isAuthenticated" module="inboxActiveChat" />
        <tour v-if="getInstance().isAuthenticated" :options="{ startTimeout: 2500 }" module="teamChat" />
        <tour v-if="getInstance().isAuthenticated" :options="{ startTimeout: 1000 }" module="settings" />

        <b-alert
            v-if="customerSettings.noticeEnabled && customerSettings.noticeText.length > 0"
            id="notice-text"
            class="position-absolute center text-center w-100 text-ellipsis no-wrap"
            show
            variant="danger"
        >
            {{ customerSettings.noticeText }}
        </b-alert>

        <!-- Navbar -->
        <b-navbar
            :class="[navbarTypeClass]"
            :toggleable="false"
            :variant="navbarBackgroundColor"
            class="header-navbar navbar navbar-shadow align-items-center"
        >
            <slot
                :navbarBackgroundColor="navbarBackgroundColor"
                :navbarTypeClass="[...navbarTypeClass, 'header-navbar navbar navbar-shadow align-items-center']"
                :toggleVerticalMenuActive="toggleVerticalMenuActive"
                name="navbar"
            >
                <app-navbar :toggle-vertical-menu-active="toggleVerticalMenuActive" />
            </slot>
        </b-navbar>
        <!--/ Navbar -->

        <!-- Vertical Nav Menu -->
        <vertical-nav-menu
            v-if="!isNavMenuHidden"
            :is-vertical-menu-active="isVerticalMenuActive"
            :toggle-vertical-menu-active="toggleVerticalMenuActive"
        >
            <template #header="slotProps">
                <slot name="vertical-menu-header" v-bind="slotProps" />
            </template>
        </vertical-nav-menu>
        <!-- /Vertical Nav Menu -->

        <!-- Vertical Nav Menu Overlay -->
        <div :class="overlayClasses" class="sidenav-overlay" @click="isVerticalMenuActive = false" />
        <!-- /Vertical Nav Menu Overlay -->

        <!-- Content -->

        <!-- CONTENT TYPE: Left -->
        <transition :name="routerTransition" mode="out-in">
            <component
                :is="layoutContentRenderer"
                :key="
                    layoutContentRenderer === 'layout-content-renderer-left'
                        ? $route.meta.navActiveLink || $route.name
                        : null
                "
            >
                <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
                    <slot :name="name" v-bind="data" />
                </template>
            </component>
        </transition>
        <!--/ Content -->
    </div>
</template>

<script>
import { computed, onUnmounted } from "@vue/composition-api";
import AppNavbarVerticalLayout from "@core/layouts/components/app-navbar/AppNavbarVerticalLayout.vue";
import AppFooter from "@/layouts/components/footer.vue";
import AppNavbar from "@/layouts/components/navbar.vue";
import useAppConfig from "@/composables/useAppConfig";
import { BNavbar, BAlert } from "bootstrap-vue";

import LayoutContentRendererDefault from "@core/layouts/components/layout-content-renderer/LayoutContentRendererDefault.vue";

import LayoutContentRendererLeft from "@core/layouts/components/layout-content-renderer/LayoutContentRendererLeft.vue";

import LayoutContentRendererLeftDetached from "@core/layouts/components/layout-content-renderer/LayoutContentRendererLeftDetached.vue";

import VerticalNavMenu from "./components/vertical-nav-menu/verticalNavMenu.vue";
import useVerticalLayout from "@core/layouts/layout-vertical/useVerticalLayout";
import mixinVerticalLayout from "@core/layouts/layout-vertical/mixinVerticalLayout";
import tour from "@/layouts/components/tour";
import deprecatedAppModal from "@/layouts/components/appModal"; // Only used for email at the moment.
import appModal from "@/views/modules/messaging-inbox/sidebar-right/components/appModal";
import { getInstance } from "@/auth/auth0";
import { useTitle } from "@vueuse/core";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import { useQuery } from "@tanstack/vue-query";
import numberLimiter from "@/utils/numberLimiter.util";
import { useCustomersStore } from "@/store/useCustomersStore";
import { storeToRefs } from "pinia";

export default {
    components: {
        // AppBreadcrumb,
        BAlert,
        AppNavbarVerticalLayout,
        AppNavbar,
        AppFooter,
        VerticalNavMenu,
        BNavbar,
        LayoutContentRendererLeftDetached,
        LayoutContentRendererLeft,
        LayoutContentRendererDefault,
        tour,
        deprecatedAppModal, // Only used for email at the moment.
        appModal,
    },
    mixins: [mixinVerticalLayout],
    computed: {
        layoutContentRenderer() {
            const rendererType = this.$route.meta.contentRenderer;
            if (rendererType === "sidebar-left") {
                return "layout-content-renderer-left";
            }
            if (rendererType === "sidebar-left-detached") {
                return "layout-content-renderer-left-detached";
            }
            return "layout-content-renderer-default";
        },
    },
    setup() {
        const { routerTransition, navbarBackgroundColor, navbarType, footerType, isNavMenuHidden } = useAppConfig();

        const { fetchUnreadChats, totalFilteredUnreadChatsCount } = useUnreadChats();
        useQuery({ queryKey: ["unreadCounters"], queryFn: fetchUnreadChats, refetchOnWindowFocus: false });
        useTitle(
            computed(() => {
                if (totalFilteredUnreadChatsCount.value > 0) {
                    return `Saysimple (${numberLimiter(
                        totalFilteredUnreadChatsCount.value,
                        3
                    )}) - Customer Communications Platform`;
                }

                return "Saysimple - Customer Communications Platform";
            })
        );

        // Fetch customer settings globally, you can use it everywhere.
        const { fetchCustomerSettings } = useCustomersStore();
        const { customerSettings } = storeToRefs(useCustomersStore());
        fetchCustomerSettings();

        const {
            isVerticalMenuActive,
            toggleVerticalMenuActive,
            isVerticalMenuCollapsed,
            layoutClasses,
            overlayClasses,
            resizeHandler,
            navbarTypeClass,
            footerTypeClass,
        } = useVerticalLayout(navbarType, footerType);

        // Resize handler
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        onUnmounted(() => {
            window.removeEventListener("resize", resizeHandler);
        });

        return {
            isVerticalMenuActive,
            toggleVerticalMenuActive,
            isVerticalMenuCollapsed,
            overlayClasses,
            layoutClasses,
            navbarTypeClass,
            footerTypeClass,

            // App Config
            routerTransition,
            navbarBackgroundColor,
            isNavMenuHidden,
            getInstance,
            customerSettings,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/base/themes/bordered-layout.scss";
@import "~@/assets/scss/tour.scss";
@import "~@/assets/scss/icons.scss";
</style>
